import React, { useEffect, useState } from "react";
import TableSkeleton from "../../../../../../../../component/TableSkeleton";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useOutletContext, useNavigate } from "react-router";
import EmptyState from "../../../../../../../../component/EmptyState";
import BtnSecondary from "../../../../../../../../component/SecondaryButton";
import { StatCard, StatusChip, headCellSx, bodyCellSx } from "../../../../../../../../component/listing";
import { getStructureByEmployee, getSalaryRevisions, getPayslipsByEmployee } from "../../../../../../../../services/payroll";
import { tokens, radius } from "../../../../../../../../theme";
import { money, periodLabel } from "../../../../payroll-new/payrollUtils";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const ProfileCompensation = () => {
  const [, employeeId] = useOutletContext();
  const navigate = useNavigate();
  const [structure, setStructure] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [s, r, p] = await Promise.all([
          getStructureByEmployee(employeeId).catch(() => null),
          getSalaryRevisions(employeeId).catch(() => null),
          getPayslipsByEmployee(employeeId).catch(() => null),
        ]);
        if (!active) return;
        setStructure(s?.data || null);
        setRevisions(Array.isArray(r?.data) ? r.data : []);
        setPayslips(Array.isArray(p?.data) ? p.data : []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [employeeId]);

  if (loading) return (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Period</TableCell>
                <TableCell align="right" sx={headCellSx}>Gross</TableCell>
                <TableCell align="right" sx={headCellSx}>Deductions</TableCell>
                <TableCell align="right" sx={headCellSx}>Net Pay</TableCell>
              </TableRow>
            </TableHead>
            <TableBody><TableSkeleton columns={4} /></TableBody>
          </Table>
        );

  // annual statement: totals for the most recent year present in payslips
  const annual = (() => {
    if (payslips.length === 0) return null;
    const year = Math.max(...payslips.map((p) => p.year));
    const yp = payslips.filter((p) => p.year === year);
    const sum = (f) => yp.reduce((s, p) => s + (Number(p[f]) || 0), 0);
    const tds = yp.reduce((s, p) => s + (p.deductions || []).filter((d) => /TDS/i.test(d.name)).reduce((a, d) => a + (Number(d.amount) || 0), 0), 0);
    return { year, months: yp.length, gross: sum("grossPay"), deductions: sum("totalDeductions"), net: sum("netPay"), tds };
  })();

  const earnings = (structure?.components || []).filter((c) => c.component !== "DEDUCTION");
  const card = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem", mb: "1.5rem" };

  return (
    <Box>
      {/* current structure */}
      {!structure ? (
        <Box sx={card}>
          <EmptyState title="No salary structure" description="Configure a salary structure for this employee." />
          <Stack direction="row" justifyContent="center" sx={{ mt: "1rem" }}>
            <BtnSecondary onClick={() => navigate(`/app/settings/payroll/structures/${employeeId}`)} sx={{ borderRadius: "8px" }}>Configure structure</BtnSecondary>
          </Stack>
        </Box>
      ) : (
        <Box sx={card}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
            <Box sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>Current Compensation</Box>
            <BtnSecondary onClick={() => navigate(`/app/settings/payroll/structures/${employeeId}`)} sx={{ borderRadius: "8px" }}>Edit</BtnSecondary>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
            <StatCard label="Annual CTC" value={money(structure.ctc)} accent={tokens.accent} />
            <StatCard label="Monthly Gross" value={money(structure.monthlyGross)} />
            <StatCard label="Monthly Net" value={money(structure.monthlyNet)} accent={tokens.success} />
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: "0.5rem 2rem" }}>
            {earnings.map((c, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" sx={{ py: "0.5rem", borderBottom: `1px solid ${tokens.border}` }}>
                <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>{c.name}</Box>
                <Box sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{money(c.monthlyAmount)}/mo</Box>
              </Stack>
            ))}
          </Box>
        </Box>
      )}

      {/* revision timeline */}
      <Box sx={card}>
        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "1rem" }}>Salary Revision History</Box>
        {revisions.length === 0 ? (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>No revisions recorded yet.</Box>
        ) : (
          <Stack spacing={0}>
            {revisions.map((r, i) => {
              const up = r.changeAmount > 0, down = r.changeAmount < 0;
              return (
                <Stack key={r._id || i} direction="row" alignItems="center" spacing={1.5}
                  sx={{ py: "1rem", borderBottom: i < revisions.length - 1 ? `1px solid ${tokens.border}` : "none" }}>
                  <Box sx={{ width: "6.5rem", fontSize: "0.8125rem", color: tokens.textMuted }}>{fmt(r.effectiveFrom)}</Box>
                  <Box sx={{ flex: 1, fontSize: "0.9375rem", fontWeight: 600 }}>{money(r.ctc)} <Box component="span" sx={{ fontWeight: 500, color: tokens.textMuted, fontSize: "0.8125rem" }}>/ year</Box></Box>
                  {r.previousCtc > 0 ? (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: up ? tokens.successText : down ? tokens.danger : tokens.textMuted, fontSize: "0.8125rem", fontWeight: 600 }}>
                      {up && <ArrowUpwardIcon sx={{ fontSize: "0.875rem" }} />}
                      {down && <ArrowDownwardIcon sx={{ fontSize: "0.875rem" }} />}
                      {r.changePct > 0 ? "+" : ""}{r.changePct}% ({money(r.changeAmount)})
                    </Stack>
                  ) : (
                    <StatusChip label="Initial" tone="muted" />
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* annual statement */}
      {annual && (
        <Box sx={card}>
          <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "1rem" }}>Annual Statement ({annual.year}) · {annual.months} month(s) paid</Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <StatCard label="Gross earnings" value={money(annual.gross)} />
            <StatCard label="Total deductions" value={money(annual.deductions)} accent={tokens.danger} />
            <StatCard label="Tax (TDS)" value={money(annual.tds)} accent={tokens.warning} />
            <StatCard label="Net paid" value={money(annual.net)} accent={tokens.success} />
          </Stack>
        </Box>
      )}

      {/* payslip history */}
      <Box sx={card}>
        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "1rem" }}>Payslips</Box>
        {payslips.length === 0 ? (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>No payslips generated yet.</Box>
        ) : (
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Period</TableCell>
                <TableCell align="right" sx={headCellSx}>Gross</TableCell>
                <TableCell align="right" sx={headCellSx}>Deductions</TableCell>
                <TableCell align="right" sx={headCellSx}>Net Pay</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payslips.map((p) => (
                <TableRow key={p._id} hover onClick={() => navigate(`/app/settings/payroll/payslip/${p._id}`)}
                  sx={{ cursor: "pointer", "&:hover": { background: tokens.surfaceSubtle } }}>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{periodLabel(p.month, p.year)}</TableCell>
                  <TableCell align="right" sx={{ ...bodyCellSx, color: tokens.textMuted }}>{money(p.grossPay)}</TableCell>
                  <TableCell align="right" sx={{ ...bodyCellSx, color: tokens.danger }}>{money(p.totalDeductions)}</TableCell>
                  <TableCell align="right" sx={{ ...bodyCellSx, fontWeight: 600 }}>{money(p.netPay)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
};

export default ProfileCompensation;
